import React from "react";
import { ChevronRight, Sparkles, Award, Utensils, Euro, Leaf, Download, Check } from "lucide-react";
import { restaurant, menu, formulas } from "../mock";
import { downloadMenuPDF } from "../lib/menuPdf";

const About = () => (
  <section id="a-propos" className="py-8 border-b border-gray-100">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">À propos</h2>
    <p className="text-gray-700 leading-relaxed mb-6">{restaurant.description}</p>

    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <FeatureItem icon={Utensils} label="Type de cuisine" value="Thaïlandais" />
      <FeatureItem icon={Euro} label="Prix moyen" value={`${restaurant.avgPrice} €`} />
      <FeatureItem icon={Leaf} label="Choix alimentaires" value="Produits locaux" />
      <FeatureItem icon={Award} label="Distinction" value="Meilleur Hôte" />
    </div>
  </section>
);

const FeatureItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
    <Icon className="h-5 w-5 text-[#1B7B71] mt-0.5 flex-shrink-0" />
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  </div>
);

const MenuPreview = () => {
  const signatures = (menu.Plats?.items || []).filter((p) => p.signature);
  return (
    <section id="menu" className="py-8 border-b border-gray-100">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold text-gray-900">Nos formules</h2>
        <span className="text-sm text-gray-500">Servies midi & soir</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {formulas.map((f, idx) => (
          <div
            key={idx}
            className="relative rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div
              className="px-5 py-4 flex items-center justify-between"
              style={{ background: `${f.accent}0d`, borderBottom: `1px solid ${f.accent}33` }}
            >
              <div>
                <div className="text-xs uppercase tracking-widest font-bold" style={{ color: f.accent }}>
                  Formule
                </div>
                <div className="text-lg font-bold text-gray-900 leading-tight">{f.title}</div>
                {f.subtitle && (
                  <div className="text-xs text-gray-600 mt-0.5">{f.subtitle}</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold" style={{ color: f.accent }}>
                  {f.price} €
                </div>
                <div className="text-[11px] text-gray-500">par personne</div>
              </div>
            </div>
            <div className="p-5 space-y-5">
              {f.sections.map((s, si) => (
                <div key={si}>
                  <div className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: f.accent }} />
                    {s.title}
                  </div>
                  <ul className="space-y-1.5">
                    {s.items.map((it, ii) => (
                      <li key={ii} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: f.accent }} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  {s.note && (
                    <p className="text-xs italic text-gray-500 mt-2 pl-6">{s.note}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-3">Nos plats signature à la carte</h3>
      <div className="space-y-3 mb-5">
        {signatures.map((d, i) => (
          <div key={i} className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-200 hover:border-[#1B7B71] hover:shadow-sm transition-all">
            <div>
              <div className="font-semibold text-gray-900">{d.name}</div>
              {d.desc && <div className="text-sm text-gray-600 mt-1">{d.desc}</div>}
            </div>
            <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">{d.price} €</div>
          </div>
        ))}
      </div>

      <button
        onClick={() => document.getElementById("full-menu")?.scrollIntoView({ behavior: "smooth" })}
        className="flex items-center gap-1 text-[#1B7B71] font-semibold text-sm hover:underline"
      >
        Parcourir le menu complet <ChevronRight className="h-4 w-4" />
      </button>
    </section>
  );
};

const DishRow = ({ d }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-0">
    <div className="flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-900">{d.name}</span>
        {d.signature && (
          <span className="text-[10px] uppercase tracking-wide bg-[#FDECE3] text-[#D14318] px-2 py-0.5 rounded-full font-bold">Signature</span>
        )}
        {d.suffix && (
          <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">{d.suffix}</span>
        )}
      </div>
      {d.desc && <div className="text-sm text-gray-600 mt-1">{d.desc}</div>}
    </div>
    <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">{d.price} €</div>
  </div>
);

const FullMenu = () => {
  const categories = Object.keys(menu);
  const [active, setActive] = React.useState(categories[0]);
  const cat = menu[active];
  return (
    <section id="full-menu" className="py-8 border-b border-gray-100">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="text-2xl font-bold text-gray-900">Menu complet</h2>
        <button
          onClick={downloadMenuPDF}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B7B71] text-white text-sm font-semibold rounded-full hover:bg-[#155f57] transition-colors shadow-sm"
        >
          <Download className="h-4 w-4" />
          Télécharger le menu (PDF)
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setActive(c)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              active === c
                ? "bg-[#1B7B71] text-white border-[#1B7B71]"
                : "bg-white text-gray-700 border-gray-200 hover:border-[#1B7B71] hover:text-[#1B7B71]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {cat.priceHint && (
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-[#E6F3F1] text-[#1B7B71] text-xs font-semibold">
          <Sparkles className="h-3.5 w-3.5" /> {cat.priceHint}
        </div>
      )}

      {cat.items && (
        <div className="space-y-1">
          {cat.items.map((d, i) => <DishRow key={i} d={d} />)}
        </div>
      )}

      {cat.groups && (
        <div className="space-y-6">
          {cat.groups.map((g, gi) => (
            <div key={gi}>
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#1B7B71] mb-2 pb-2 border-b border-[#1B7B71]/20">
                {g.title}
              </h3>
              <div className="space-y-1">
                {g.items.map((d, i) => <DishRow key={i} d={d} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {cat.note && (
        <p className="text-sm italic text-gray-600 mt-4 pt-3 border-t border-gray-100">
          <Sparkles className="inline h-4 w-4 text-[#1B7B71] mr-1 -mt-0.5" />
          {cat.note}
        </p>
      )}
    </section>
  );
};

export { About, MenuPreview, FullMenu };
